import { useCallback, useEffect, useState } from 'react'

import { useNavigation } from '~/hooks/useNavigation'
import { useCookieStore } from '~/store/cookieStore'
import { AuthUser, BackendSmartAccount } from '~/types'

type ReconcileActiveOrgState =
  | { kind: 'idle' }
  | {
      kind: 'prompt'
      ownerOrgName: string | undefined
      onConfirm: () => void
      onCancel: () => void
    }

/**
 * When a user lands on an account they have access to via a different org
 * than is currently active (e.g. a deep link from an email or the CLI), the
 * caller should render a confirmation dialog using the returned state. If
 * the user confirms, the active-org cookie flips and the deep-linked account
 * loads. If they cancel, they're sent back to the dashboard for their
 * current org.
 */
export const useReconcileActiveOrgForAccount = ({
  authUser,
  smartAccount,
}: {
  authUser: AuthUser | undefined
  smartAccount: BackendSmartAccount | undefined
}): {
  isReconciling: boolean
  state: ReconcileActiveOrgState
} => {
  const [hasResolved, setHasResolved] = useState(false)
  // Defer prompting by one paint: when the user manually switches orgs via the
  // org switcher, AppContainer's <Fragment key={activeOrgId}> remounts this
  // page synchronously, but the accompanying navigate('/') is async. Without
  // this gate, the new instance briefly renders the prompt against stale
  // cached smart-account data before unmounting on the route change.
  const [readyToPrompt, setReadyToPrompt] = useState(false)
  // eslint-disable-next-line local/noUseEffect -- one-shot mount gate; useState alone can't observe paint
  useEffect(() => {
    const id = requestAnimationFrame(() => setReadyToPrompt(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const activeOrgId = useCookieStore((s) => s.activeOrgId)
  const setActiveOrgId = useCookieStore((s) => s.setActiveOrgId)
  const navigate = useNavigation()

  const ownerOrgId = smartAccount?.orgId
  const ownerOrg = authUser?.orgs.find((org) => org.id === ownerOrgId)
  const ownerOrgName = ownerOrg?.name || undefined
  const userIsOwnerMember = !!ownerOrg
  const needsSwitch =
    !!ownerOrgId &&
    !!activeOrgId &&
    activeOrgId !== ownerOrgId &&
    userIsOwnerMember

  const onConfirm = useCallback(() => {
    if (!ownerOrgId) return
    setHasResolved(true)
    setActiveOrgId(ownerOrgId)
  }, [ownerOrgId, setActiveOrgId])

  const onCancel = useCallback(() => {
    setHasResolved(true)
    navigate('/')
  }, [navigate])

  const showPrompt = readyToPrompt && needsSwitch && !hasResolved

  const state: ReconcileActiveOrgState = showPrompt
    ? { kind: 'prompt', ownerOrgName, onConfirm, onCancel }
    : { kind: 'idle' }

  return {
    isReconciling: needsSwitch,
    state,
  }
}
